import {
  addDisplayName,
  addHandlers,
  addProps,
  addRef,
  addWrapper,
  flowMax,
} from 'ad-hok'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  addIntPathParam,
  addLocation,
  addNavigate,
} from 'shared/src/utils/addNavigate'
import { Helmet } from 'react-helmet-async'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { navBackOrHomepage } from 'shared/src/utils/navBack'
import { addAuthContext } from 'shared/src/utils/auth'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'
import { ReactComponent as RodeoLogo } from 'shared/src/assets/rodeo-logo.svg'

import { Media, MediaState, invertState } from './fluid/Media'
import { Footer } from 'components/footer'
import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addShowRegisterContext } from 'utils/register'
import {
  RodeoOrEditViewType,
  addRodeoOrEditViewContextProvider,
} from 'utils/rodeoOrEditViewContext'
import { makeCreatorProfileUrl } from 'utils/routes'
import { FLUID_CONTAINER_STYLE } from 'utils/styles'
import { setPageTitle } from 'utils/title'
import { NAVBAR_Z_INDEX } from 'utils/zIndex'
import { ConstrainWidth } from '../../utils/layout'
import { addFeedContextProvider } from './context'
import { ImageButton } from 'components/ImageButton'
import backButtonIcon from 'assets/caret-left.svg'

const useStyles = createUseStyles({
  container: {
    height: '100%',
    ...FLUID_CONTAINER_STYLE,
  },
  header: {
    position: 'relative',
  },
  roundupButton: {
    position: 'absolute',
    top: 16,
    right: 34,
    zIndex: NAVBAR_Z_INDEX,
  },
  mediaContainer: {
    height: '100%',
    width: '100%',
  },
  mediaItem: {
    scrollSnapAlign: 'start',
    height: '100%',
  },
  navBarContainer: {
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: '20px',
  },
  logoContainer: {
    position: 'fixed',
    top: 0,
    zIndex: NAVBAR_Z_INDEX,
    marginTop: 15,
    width: '100vw',
    maxWidth: '428px',
    display: 'flex',
    justifyContent: 'center',
  },
  loading: {
    position: 'relative',
    color: '#ffffff',
    fontSize: 30,
    top: '50%',
  },
  wantMore: {
    scrollSnapAlign: 'end',
  },
})
interface Props {}
export const SingleMedia: FC<Props> = flowMax(
  addDisplayName('SingleMedia'),
  addIntPathParam('mediaId'),
  addWrapper((render, { mediaId }) => (
    <React.Fragment key={`Media-${mediaId}`}>{render()}</React.Fragment>
  )),
  addApi,
  addAuthContext,
  addDataLoadingFromPromise(
    'data',
    ({ mediaId, api }) => api.media.getFeedItem(mediaId),
    ['mediaId'],
    ({ mediaId }) => `singleMediaData-${mediaId}`
  ),
  addLoadingAndErrorIndicators('data', {
    redirectTo404IfError: true,
  }),
  addProps(({ data }) => ({
    media: data[0],
  })),
  addProps({
    rodeoOrEditView: typedAs<RodeoOrEditViewType>('rodeo'),
  }),
  addRodeoOrEditViewContextProvider,
  addTranslationHelpers,
  addProps(({ media }) => ({
    title: `${media.user.username}'s post #${media.id}"${
      media.caption ? ' - "' + media.caption + '"' : ''
    }"`,
  })),
  setPageTitle(({ title }) => title ?? null),
  addNavigationState('singleMedia', 'singleMedia', ({ mediaId }) => mediaId),
  addRef('scrollableContainerRef', typedAs<HTMLDivElement | null>(null)),
  addFeedContextProvider,
  addLocation,
  addNavigate,
  addNavigationState('feedState', 'setFeedState', typedAs<MediaState>('rodeo')),
  addNavigationState('showBackButton', 'setShowBackButton', false),
  addHandlers({
    onClickCurrentTab:
      ({ scrollableContainerRef }) =>
      () => {
        scrollableContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      },
    onClickBack:
      ({ navigate, media, location, showBackButton }) =>
      () => {
        if (showBackButton) {
          navBackOrHomepage()
        } else if (media.user.username) {
          navigate(makeCreatorProfileUrl(media.user.username, 'posts'), {
            state: {
              ...location.state,
            },
            replace: true,
          })
        }
      },
  }),
  addShowRegisterContext,
  addProps(() => ({
    classes: useStyles(),
  })),
  addHandlers(
    {
      onToggleState:
        ({ feedState, setFeedState }) =>
        () => {
          setFeedState(invertState(feedState))
        },
    },
    ['feedState', 'setFeedState']
  ),
  ({
    onToggleState,
    feedState,
    media,
    onClickBack,
    navigate,
    scrollableContainerRef,
    classes,
  }) => (
    <>
      <Helmet>
        <meta property="og:type" content="article" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_SHOPPER_URL}/medias/${media.id}`.replace(
            /(?<!:)\/+/g,
            '/'
          )}
        />
      </Helmet>
      <div ref={scrollableContainerRef} className={classes.container}>
        <ConstrainWidth>
          <div className={classes.logoContainer}>
            <ImageButton
              className={classes.backButton}
              image={backButtonIcon}
              width={20}
              height={20}
              onClick={onClickBack}
            />
            <RodeoLogo
              onClick={() => navigate('/')}
              style={{
                cursor: 'pointer',
                fill: 'white',
                width: 72,
                height: 36,
              }}
            />
          </div>
        </ConstrainWidth>
        <ConstrainWidth
          key={`media-${media.id}`}
          className={classes.mediaContainer}
        >
          <Media
            noVisibleMediaTrackThruNavigation={true}
            includeProdDescOnMediaAlt={true}
            media={media}
            state={feedState}
            onToggleState={onToggleState}
            scrollableContainerElement={scrollableContainerRef.current}
            className={classes.mediaItem}
          />
        </ConstrainWidth>
        <ConstrainWidth>
          <Footer className={classes.wantMore} />
        </ConstrainWidth>
      </div>
    </>
  )
)
